import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { apiEvents, ApiErrorEvent } from '@/utils/apiEvents'
import { useSwal } from '@/hooks/useSwal'

/**
 * Bridges global API error events (emitted by the axios interceptors in
 * services/api.ts) to visible useSwal toasts. Without a subscriber those
 * mitt events were invisible — server errors, rate limits and network
 * failures never reached the user.
 */
export default function ApiEventToaster() {
  const { t } = useTranslation()
  const swal = useSwal()

  // useSwal()/useTranslation() hand back a fresh object each render, so the
  // latest values are kept in a ref: the mitt listener registers exactly once
  // instead of being torn down and re-added on every render (which dropped
  // events fired between the off/on pair and reset the dedup window).
  const latestRef = useRef({ swal, t })
  useEffect(() => {
    latestRef.current = { swal, t }
  }, [swal, t])

  useEffect(() => {
    const lastShown = new Map<string, number>()
    const DEDUP_WINDOW_MS = 1500

    const handler = (event: ApiErrorEvent) => {
      const { swal: toast, t: translate } = latestRef.current
      const key = `${event.type ?? 'error'}:${event.title ?? ''}:${event.message}`
      const now = Date.now()
      // Keep the map bounded: entries only matter within the dedup window, so
      // evict anything older instead of accumulating for the session lifetime.
      for (const [seenKey, seenAt] of lastShown) {
        if (now - seenAt >= DEDUP_WINDOW_MS) lastShown.delete(seenKey)
      }
      const last = lastShown.get(key) ?? 0
      if (now - last < DEDUP_WINDOW_MS) return
      lastShown.set(key, now)

      const prefix = event.title && event.title !== translate('Error') ? `${event.title}: ` : ''
      // Events cross the mitt boundary untyped, so a missing message would
      // otherwise render the literal "undefined" to the user.
      const message = event.message?.trim() ? event.message : translate('Error')
      toast.notify(event.type ?? 'error', `${prefix}${message}`)
    }

    apiEvents.on('error', handler)
    return () => {
      apiEvents.off('error', handler)
    }
  }, [])

  return null
}
